/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import _toNumber from "lodash/toNumber";
import _slice from "lodash/slice";
import _cloneDeep from "lodash/cloneDeep";
import { Box, MenuItem } from "@mui/material";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import { requiredValidator } from "../../../services/validationRules";
import OtherAsset from "./OtherAsset/OtherAsset";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";

type CashValuablesProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function CashValuables({ submitProps, product }: CashValuablesProps) {
  const { values, handleChange, handleSubmit } = useForm();
  useEffect(() => {
    const numberOfOtherAssets = values.numberOfOtherAssets ? _toNumber(values.numberOfOtherAssets) : 0;
    if (values.otherAssets.length === numberOfOtherAssets) {
      return;
    }
    const emptyPensionInsurance = {
      name: "",
      policyHolder: "",
      date: "",
      value: "",
    };
    let otherAssets = _cloneDeep(values.otherAssets);
    for (let i = otherAssets.length; i < Math.min(numberOfOtherAssets, 5); i++) {
      otherAssets.push(_cloneDeep(emptyPensionInsurance));
    }
    otherAssets = _slice(otherAssets, 0, numberOfOtherAssets);
    handleChange({
      target: {
        type: "other",
        name: "otherAssets",
        value: otherAssets,
      },
    });
  }, [values.otherAssets, values.numberOfOtherAssets]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Vermögen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasOtherAssets"}
                question={"Haben Sie weitere Vermögenswerte?"}
                modalContent={<p>Zum Beispiel Bausparverträge, Wertpapiere, Beteiligungen an Gesellschaften oder Forderungen gegen Dritte.</p>}
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasOtherAssets === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie viele weitere Vermögenswerte haben Sie?"
                  name="numberOfOtherAssets"
                  validators={[requiredValidator("Bitte geben Sie an wie viele weitere Vermögenswerte Sie haben")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </ValidatorSelectField>
                {values.otherAssets?.map((vehicle: any, index: number) => (
                  <OtherAsset key={index} index={index} path={`otherAssets[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
